import { MIME_TYPES } from "@excalidraw/common";

import {
  decryptData,
  IV_LENGTH_BYTES,
} from "@excalidraw/excalidraw/data/encryption";

import { getSceneVersion, restoreElements } from "@excalidraw/excalidraw";

import { decompressData } from "@excalidraw/excalidraw/data/encode";

import type { ExcalidrawElement, FileId } from "@excalidraw/element/types";

import type {
  AppState,
  BinaryFileData,
  BinaryFileMetadata,
  DataURL,
} from "@excalidraw/excalidraw/types";

import { httpStorageBackendUrl } from "../sym/constants";

import { encryptElements } from "./firebase";

import { getSyncableElements } from "./index";

import type Portal from "../collab/Portal";

import type { SyncableExcalidrawElement } from "./index";

import type { Socket } from "socket.io-client";

// symmedia custom
// There is a lot of intentional duplication with the firebase file
// to prevent modifying upstream files and ease future maintenance of this fork

const httpStorageSceneVersionCache = new WeakMap<Socket, number>();

export const isSavedToHttpStorage = (
  portal: Portal,
  elements: readonly ExcalidrawElement[],
): boolean => {
  if (portal.socket && portal.roomId && portal.roomKey) {
    const sceneVersion = getSceneVersion(elements);

    return httpStorageSceneVersionCache.get(portal.socket) === sceneVersion;
  }
  // if no room exists, consider the room saved so that we don't unnecessarily
  // prevent unload (there's nothing we could do at that point anyway)
  return true;
};

export const saveToHttpStorage = async (
  portal: Portal,
  elements: readonly SyncableExcalidrawElement[],
  appState: AppState,
  authToken?: string,
) => {
  const { roomId, roomKey, socket } = portal;
  if (
    // if no room exists, consider the room saved because there's nothing we can
    // do at this point
    !roomId ||
    !roomKey ||
    !socket ||
    isSavedToHttpStorage(portal, elements)
  ) {
    return elements as SyncableExcalidrawElement[];
  }

  const sceneVersion = getSceneVersion(elements);

  const getResponse = await fetch(`${httpStorageBackendUrl}/rooms/${roomId}`, {
    headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
  });
  if (!getResponse.ok && getResponse.status !== 404) {
    return null;
  }

  // If room already exist, we compare scene versions to check
  // if we're up to date before saving our scene
  if (getResponse.ok) {
    const buffer = await getResponse.arrayBuffer();
    const prevStoredElements = getSyncableElements(
      restoreElements(await getElementsFromBuffer(buffer, roomKey), null),
    );
    const prevStoredSceneVersion = getSceneVersion(prevStoredElements);

    if (prevStoredSceneVersion >= sceneVersion) {
      return null;
    }
  }

  const { ciphertext, iv } = await encryptElements(roomKey, elements);

  // Concatenate IV with encrypted data (IV does not have to be secret).
  const payloadBlob = new Blob([iv.buffer, ciphertext]);
  const payload = await new Response(payloadBlob).arrayBuffer();
  const putResponse = await fetch(`${httpStorageBackendUrl}/rooms/${roomId}`, {
    method: "PUT",
    body: payload,
    headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
  });

  if (putResponse.ok) {
    httpStorageSceneVersionCache.set(socket, sceneVersion);
    return elements as SyncableExcalidrawElement[];
  }

  return null;
};

export const loadFromHttpStorage = async (
  roomId: string,
  roomKey: string,
  socket: Socket | null,
  authToken?: string,
): Promise<readonly SyncableExcalidrawElement[] | null> => {
  const getResponse = await fetch(`${httpStorageBackendUrl}/rooms/${roomId}`, {
    headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
  });

  const buffer = await getResponse.arrayBuffer();
  const elements = await getElementsFromBuffer(buffer, roomKey);

  if (socket) {
    httpStorageSceneVersionCache.set(socket, getSceneVersion(elements));
  }

  return getSyncableElements(restoreElements(elements, null));
};

//use old decryptElements function - the new one in firebase.ts is not compatible as its data object is of type FirebaseStoredScene
const decryptElements = async (
  key: string,
  iv: Uint8Array,
  ciphertext: ArrayBuffer | Uint8Array,
): Promise<readonly ExcalidrawElement[]> => {
  const decrypted = await decryptData(iv, ciphertext, key);
  const decodedData = new TextDecoder("utf-8").decode(
    new Uint8Array(decrypted),
  );
  return JSON.parse(decodedData);
};

const getElementsFromBuffer = async (
  buffer: ArrayBuffer,
  key: string,
): Promise<readonly ExcalidrawElement[]> => {
  // Buffer should contain both the IV (fixed length) and encrypted data
  const iv = buffer.slice(0, IV_LENGTH_BYTES);
  const encrypted = buffer.slice(IV_LENGTH_BYTES, buffer.byteLength);

  return await decryptElements(
    key,
    new Uint8Array(iv),
    new Uint8Array(encrypted),
  );
};

export const saveFilesToHttpStorage = async ({
  prefix,
  files,
  roomId,
  authToken,
}: {
  prefix: string;
  files: { id: FileId; buffer: Uint8Array }[];
  roomId?: string;
  authToken?: string;
}) => {
  const erroredFiles: FileId[] = [];
  const savedFiles: FileId[] = [];

  await Promise.all(
    files.map(async ({ id, buffer }) => {
      try {
        const payloadBlob = new Blob([buffer]);
        const payload = await new Response(payloadBlob).arrayBuffer();

        await fetch(`${httpStorageBackendUrl}/files/${roomId}/${id}`, {
          method: "PUT",
          body: payload,
          headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
        });
        savedFiles.push(id);
      } catch (error: any) {
        erroredFiles.push(id);
      }
    }),
  );

  return { savedFiles, erroredFiles };
};

export const loadFilesFromHttpStorage = async (
  prefix: string,
  decryptionKey: string,
  filesIds: readonly FileId[],
  roomId?: string,
  authToken?: string,
) => {
  const loadedFiles: BinaryFileData[] = [];
  const erroredFiles = new Map<FileId, true>();

  await Promise.all(
    [...new Set(filesIds)].map(async (id) => {
      try {
        const response = await fetch(
          `${httpStorageBackendUrl}/files/${roomId}/${id}`,
          {
            headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
          },
        );
        if (response.status < 400) {
          const arrayBuffer = await response.arrayBuffer();

          const { data, metadata } = await decompressData<BinaryFileMetadata>(
            new Uint8Array(arrayBuffer),
            {
              decryptionKey,
            },
          );

          const dataURL = new TextDecoder().decode(data) as DataURL;

          loadedFiles.push({
            mimeType: metadata.mimeType || MIME_TYPES.binary,
            id,
            dataURL,
            created: metadata?.created || Date.now(),
          });
        } else {
          erroredFiles.set(id, true);
        }
      } catch (error: any) {
        erroredFiles.set(id, true);
        console.error(error);
      }
    }),
  );

  return { loadedFiles, erroredFiles };
};
